import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Clock, PackageCheck, Store, XCircle } from 'lucide-react';
import { toast } from '../../contexts/ToastContext';
import { neo4jService } from '../../services/neo4jService';
import './ManagePickupOrders.css';

const getPickupOrderImage = (order) => order.productImage || order.main_image || order.image || '';

const PICKUP_STATUSES = [
  { value: 'requested', label: 'Pickup Requested', icon: Clock },
  { value: 'ready', label: 'Ready for Pickup', icon: PackageCheck },
  { value: 'picked_up', label: 'Picked Up', icon: CheckCircle },
  { value: 'cancelled', label: 'Cancelled', icon: XCircle }
];

const statusLabels = PICKUP_STATUSES.reduce((labels, status) => ({
  ...labels,
  [status.value]: status.label
}), {});

function formatPickupSchedule(order) {
  if (order.pickupScheduledAt) {
    return new Date(order.pickupScheduledAt).toLocaleString([], {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }

  if (order.pickupDate && order.pickupTime) {
    return new Date(`${order.pickupDate}T${order.pickupTime}`).toLocaleString([], {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }

  return 'No pickup time selected';
}

function ManagePickupOrders() {
  const [pickupOrders, setPickupOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingOrderKey, setUpdatingOrderKey] = useState('');

  const pickupCounts = useMemo(() => {
    return pickupOrders.reduce((counts, order) => {
      const status = order.pickupStatus || 'requested';
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    }, {});
  }, [pickupOrders]);

  useEffect(() => {
    fetchPickupOrders();
  }, []);

  async function fetchPickupOrders() {
    try {
      setLoading(true);
      const orders = await neo4jService.getPickupOrders();
      setPickupOrders(orders);
    } catch (error) {
      console.error('Failed to fetch pickup orders:', error);
      toast.error('Failed to load pickup orders');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(order, pickupStatus) {
    const orderKey = `${order.orderId}-${order.productId}`;
    try {
      setUpdatingOrderKey(orderKey);
      await neo4jService.updatePickupOrderStatus(order.orderId, order.productId, pickupStatus);
      setPickupOrders(orders =>
        orders.map(item =>
          item.orderId === order.orderId && item.productId === order.productId
            ? { ...item, pickupStatus, status: statusLabels[pickupStatus] }
            : item
        )
      );
      toast.success('Pickup status updated');
      await fetchPickupOrders();
    } catch (error) {
      console.error('Failed to update pickup status:', error);
      toast.error('Failed to update pickup status');
    } finally {
      setUpdatingOrderKey('');
    }
  }

  async function handleRestockCancelledOrder(order) {
    const orderKey = `${order.orderId}-${order.productId}`;
    try {
      setUpdatingOrderKey(orderKey);
      await neo4jService.restockCancelledPickupOrder(order.orderId, order.productId);
      toast.success('Cancelled order stock restored');
      await fetchPickupOrders();
    } catch (error) {
      console.error('Failed to restock cancelled order:', error);
      toast.error('Failed to restock cancelled order');
    } finally {
      setUpdatingOrderKey('');
    }
  }

  if (loading) {
    return (
      <div className="manage-pickup-orders">
        <h2>Pickup Orders</h2>
        <div className="pickup-empty-state">Loading pickup orders...</div>
      </div>
    );
  }

  return (
    <div className="manage-pickup-orders">
      <div className="pickup-admin-header">
        <div>
          <h2>Pickup Orders</h2>
          <p>Track in-store pickup requests from checkout.</p>
        </div>
        <button className="refresh-pickups-button" onClick={fetchPickupOrders}>
          Refresh
        </button>
      </div>

      <div className="pickup-summary-grid">
        {PICKUP_STATUSES.map(({ value, label, icon: Icon }) => (
          <div key={value} className="pickup-summary-card">
            <Icon size={20} />
            <span>{label}</span>
            <strong>{pickupCounts[value] || 0}</strong>
          </div>
        ))}
      </div>

      {pickupOrders.length === 0 ? (
        <div className="pickup-empty-state">
          <Store size={40} />
          <h3>No pickup orders yet</h3>
          <p>Pickup requests will appear here after customers place them from the cart.</p>
        </div>
      ) : (
        <div className="pickup-orders-list">
          {pickupOrders.map(order => {
            const orderKey = `${order.orderId}-${order.productId}`;
            const selectedStatus = order.pickupStatus || 'requested';
            const lineTotal = Number(order.price || 0) * Number(order.quantity || 1);
            const orderImage = getPickupOrderImage(order);

            return (
              <div key={orderKey} className="pickup-order-row">
                {orderImage ? (
                  <img src={orderImage} alt={order.productName} />
                ) : (
                  <div className="pickup-image-placeholder">
                    <Store size={28} />
                  </div>
                )}
                <div className="pickup-order-main">
                  <div className="pickup-order-title-row">
                    <h3>{order.productName}</h3>
                    <span className={`pickup-status-badge ${selectedStatus}`}>
                      {statusLabels[selectedStatus] || order.status}
                    </span>
                  </div>
                  <div className="pickup-order-meta">
                    <span>Order #{order.orderId}</span>
                    <span>{new Date(order.orderDate).toLocaleString()}</span>
                    <span>Qty {order.quantity}</span>
                    <span>${lineTotal.toFixed(2)}</span>
                  </div>
                  <div className="pickup-schedule">
                    Pickup: {formatPickupSchedule(order)}
                  </div>
                  <div className="pickup-contact">
                    <strong>{order.pickupName || order.customerName}</strong>
                    <span>{order.pickupPhone || 'No phone provided'}</span>
                    <span>{order.pickupEmail || order.customerEmail}</span>
                  </div>
                  {order.pickupNotes && (
                    <p className="pickup-notes">{order.pickupNotes}</p>
                  )}
                </div>
                <label className="pickup-status-control">
                  Status
                  <select
                    value={selectedStatus}
                    disabled={updatingOrderKey === orderKey}
                    onChange={(event) => handleStatusChange(order, event.target.value)}
                  >
                    {PICKUP_STATUSES.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {selectedStatus === 'cancelled' && !order.stockRestored && (
                    <button
                      type="button"
                      className="restock-pickup-button"
                      disabled={updatingOrderKey === orderKey}
                      onClick={() => handleRestockCancelledOrder(order)}
                    >
                      Restore Stock
                    </button>
                  )}
                  {selectedStatus === 'cancelled' && order.stockRestored && (
                    <span className="stock-restored-label">Stock restored</span>
                  )}
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ManagePickupOrders;
